import { useEffect } from "react";

declare global {
	interface Window {
		$chatwoot?: {
			setUser: (id: string, userData: Record<string, string>) => void;
			setCustomAttributes: (attrs: Record<string, string>) => void;
		};
	}
}

interface ChatwootWidgetProps {
	user?: {
		id: string;
		email: string;
		displayName?: string | null;
		firstName?: string | null;
		lastName?: string | null;
		role?: string | null;
	};
	clubSlug?: string;
	clubPermissions?: Array<{ slug: string }>;
}

export function ChatwootWidget({
	user,
	clubSlug,
	clubPermissions,
}: ChatwootWidgetProps) {
	useEffect(() => {
		if (!user || typeof window === "undefined") return;

		const handleChatwootReady = () => {
			// Build user data object
			const userData: Record<string, string> = {};

			if (user.email) {
				userData.email = user.email;
			}

			// Name: prioritize displayName, otherwise firstName + lastName
			const name =
				user.displayName ||
				[user.firstName, user.lastName].filter(Boolean).join(" ");
			if (name) {
				userData.name = name;
			}

			// Set user in Chatwoot (only if we have email)
			if (user.email && window.$chatwoot) {
				window.$chatwoot.setUser(user.id, userData);
			}

			// Determine user role based on club permissions
			let userRole = "user";
			if (clubPermissions?.some((p) => p.slug === "CLUB_ADMIN")) {
				userRole = "admin";
			} else if (clubPermissions?.some((p) => p.slug === "MODERATOR")) {
				userRole = "moderator";
			}

			// Custom attributes (always set)
			if (window.$chatwoot) {
				window.$chatwoot.setCustomAttributes({
					scarf_email: user.email, // Real email (immutable)
					user_role: userRole,
					...(clubSlug && { club_slug: clubSlug }),
				});
			}
		};

		// Listen to chatwoot:ready event
		window.addEventListener("chatwoot:ready", handleChatwootReady);

		// Cleanup
		return () => {
			window.removeEventListener("chatwoot:ready", handleChatwootReady);
		};
	}, [user, clubSlug, clubPermissions]);

	return null; // Component doesn't render anything
}
